import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './UsersPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const STATUS_OPTIONS = ['Aktif', 'Pasif'];

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Kullanicilar',
    pageDescription: 'Sistem kullanicilari ve roller.',
    newUser: 'Yeni kullanici',
    name: 'Isim',
    email: 'E-posta',
    role: 'Rol',
    status: 'Durum',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noUsers: 'Henuz kullanici bulunamadi.',
    edit: 'Duzenle',
    delete: 'Sil',
    noPermission: 'Yetki yok',
    close: 'Kapat',
    username: 'Kullanici adi',
    password: 'Sifre',
    rolePlaceholder: 'Seciniz',
    companyPlaceholder: 'Sirket sec',
    accessType: 'Erisim tipi',
    addCompanyAccess: '+ Sirket Erisimi',
    companyAccesses: 'Sirket Erisimleri',
    noAccessEntries: 'Bu kullaniciya ait sirket erisimi tanimli degil.',
    saveAccesses: 'Erisimleri kaydet',
    remove: 'Kaldir',
    save: 'Kaydet',
    update: 'Guncelle',
    cancel: 'Iptal',
    saving: 'Kaydediliyor...',
    passwordHint: 'Sifreyi guncellemek istemiyorsaniz alani bos birakabilirsiniz.',
    createUserAria: 'Yeni kullanici olustur',
    editUserAria: 'Kullaniciyi duzenle',
    createUserTitle: 'Yeni kullanici olustur',
    editUserTitle: 'Kullaniciyi duzenle',
    active: 'Aktif',
    passive: 'Pasif',
    tenantAdminOnlyEdit: 'Tenant admin sadece TENANT_USER kullanicilarini duzenleyebilir.',
    tenantAdminOnlyDelete: 'Tenant admin sadece TENANT_USER kullanicilarini silebilir.',
    confirmDelete: 'Bu kullaniciyi silmek istediginizden emin misiniz?',
    requiredFields: 'Lutfen zorunlu alanlari doldurun.',
    usersLoadError: 'Kullanicilar yuklenemedi.',
    usersLoadErrorDetail: 'Kullanicilar yuklenirken hata olustu.',
    rolesLoadError: 'Roller yuklenemedi.',
    rolesLoadErrorDetail: 'Roller yuklenirken hata olustu.',
    companiesLoadError: 'Sirketler yuklenemedi.',
    companiesLoadErrorDetail: 'Sirketler yuklenirken hata olustu.',
    userAccessLoadError: 'Sirket erisimleri yuklenemedi.',
    userAccessLoadErrorDetail: 'Sirket erisimleri yuklenirken hata olustu.',
    saveFailed: 'Kayit islemi basarisiz oldu.',
    operationError: 'Islem sirasinda hata olustu.',
    userUpdated: 'Kullanici guncellendi.',
    userCreated: 'Kullanici olusturuldu.',
    deleteFailed: 'Silme islemi basarisiz oldu.',
    deleteError: 'Silme sirasinda hata olustu.',
    userDeleted: 'Kullanici silindi.',
    saveUserFirst: 'Once kullaniciyi kaydedin.',
    selectCompany: 'Sirket secin.',
    invalidCompany: 'Gecersiz sirket secimi.',
    accessSaveFailed: 'Sirket erisimleri kaydedilemedi.',
    accessSaved: 'Sirket erisimleri guncellendi.',
    accessSaveError: 'Sirket erisimleri kaydedilirken hata olustu.',
    systemAdminAccess: 'Tum sistemde tam erisim yetkisine sahiptir.',
    tenantAdminAccess: 'Tenant admin kendi tanimladigi tum kullanicilar, sirketler ve donemlere varsayilan olarak eriserek bu paneli kullanmaya gerek kalmadan yetkilendirilir.',
    accessPlaceholder: 'Sirket erisimlerini tanimlamak icin once kullaniciyi kaydedip duzenleyin.',
    readOnlyLabel: 'READ_ONLY - Sadece rapor',
    readWriteLabel: 'READ_WRITE - Tam erisim',
    readOnlyDisplay: 'Sadece rapor (READ_ONLY)',
    readWriteDisplay: 'Tam erisim (READ_WRITE)',
    userLimitSummary: 'Kullanici limiti',
    userLimitUnlimited: 'Sinirsiz',
    userLimitUsage: '{{current}} / {{max}} kullaniliyor',
    userLimitUsageUnlimited: '{{current}} kullanici tanimli'
  },
  'en-US': {
    pageTitle: 'Users',
    pageDescription: 'System users and roles.',
    newUser: 'New user',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    status: 'Status',
    action: 'Action',
    loading: 'Loading...',
    noUsers: 'No users found yet.',
    edit: 'Edit',
    delete: 'Delete',
    noPermission: 'No permission',
    close: 'Close',
    username: 'Username',
    password: 'Password',
    rolePlaceholder: 'Select',
    companyPlaceholder: 'Select company',
    accessType: 'Access type',
    addCompanyAccess: '+ Company Access',
    companyAccesses: 'Company Accesses',
    noAccessEntries: 'No company access is defined for this user.',
    saveAccesses: 'Save accesses',
    remove: 'Remove',
    save: 'Save',
    update: 'Update',
    cancel: 'Cancel',
    saving: 'Saving...',
    passwordHint: 'Leave password empty if you do not want to update it.',
    createUserAria: 'Create new user',
    editUserAria: 'Edit user',
    createUserTitle: 'Create new user',
    editUserTitle: 'Edit user',
    active: 'Active',
    passive: 'Passive',
    tenantAdminOnlyEdit: 'Tenant admin can only edit TENANT_USER users.',
    tenantAdminOnlyDelete: 'Tenant admin can only delete TENANT_USER users.',
    confirmDelete: 'Are you sure you want to delete this user?',
    requiredFields: 'Please fill required fields.',
    usersLoadError: 'Users could not be loaded.',
    usersLoadErrorDetail: 'An error occurred while loading users.',
    rolesLoadError: 'Roles could not be loaded.',
    rolesLoadErrorDetail: 'An error occurred while loading roles.',
    companiesLoadError: 'Companies could not be loaded.',
    companiesLoadErrorDetail: 'An error occurred while loading companies.',
    userAccessLoadError: 'Company accesses could not be loaded.',
    userAccessLoadErrorDetail: 'An error occurred while loading company accesses.',
    saveFailed: 'Save operation failed.',
    operationError: 'An error occurred during operation.',
    userUpdated: 'User updated.',
    userCreated: 'User created.',
    deleteFailed: 'Delete operation failed.',
    deleteError: 'An error occurred during delete.',
    userDeleted: 'User deleted.',
    saveUserFirst: 'Please save user first.',
    selectCompany: 'Please select company.',
    invalidCompany: 'Invalid company selection.',
    accessSaveFailed: 'Company accesses could not be saved.',
    accessSaved: 'Company accesses updated.',
    accessSaveError: 'An error occurred while saving company accesses.',
    systemAdminAccess: 'Has full access across the entire system.',
    tenantAdminAccess: 'Tenant admin users are authorized by default for users, companies and periods they manage.',
    accessPlaceholder: 'Save user first, then edit to define company accesses.',
    readOnlyLabel: 'READ_ONLY - Report only',
    readWriteLabel: 'READ_WRITE - Full access',
    readOnlyDisplay: 'Report only (READ_ONLY)',
    readWriteDisplay: 'Full access (READ_WRITE)',
    userLimitSummary: 'User limit',
    userLimitUnlimited: 'Unlimited',
    userLimitUsage: '{{current}} / {{max}} in use',
    userLimitUsageUnlimited: '{{current}} users provisioned'
  }
};

const normalizeRoleName = (rawRole) => {
  const raw = (rawRole || '').trim();
  if (!raw) return '';
  const canonical = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (canonical === 'SYSADMIN') return 'SYS_ADMIN';
  if (canonical === 'TENANTADMIN') return 'TENANT_ADMIN';
  if (canonical === 'TENANTUSER') return 'TENANT_USER';
  return raw.toUpperCase();
};

const createFormState = () => ({
  id: null,
  username: '',
  password: '',
  fullName: '',
  email: '',
  role: '',
  status: 'Aktif',
  tenantAdmin: false,
  tenantUserMode: 'READONLY'
});

function UsersPage() {
  const { language } = useLanguage();
  const text = UI_TEXT[language] || UI_TEXT['tr-TR'];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);
  const statusLabel = useCallback((value) => (value === 'Aktif' ? t('active') : t('passive')), [t]);
  const accessOptions = useMemo(() => ([
    { value: 'READ_ONLY', label: t('readOnlyLabel') },
    { value: 'READ_WRITE', label: t('readWriteLabel') }
  ]), [t]);
  const accessLabelMap = useMemo(() => ({
    READ_ONLY: t('readOnlyDisplay'),
    READ_WRITE: t('readWriteDisplay')
  }), [t]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState(createFormState());
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyAccessEntries, setCompanyAccessEntries] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('READ_ONLY');
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [accessSuccess, setAccessSuccess] = useState('');
  const [accessDirty, setAccessDirty] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [billingInfo, setBillingInfo] = useState(null);

  const companiesById = useMemo(
    () => companies.reduce((acc, company) => ({ ...acc, [company.id]: company }), {}),
    [companies]
  );

  const buildHeaders = useCallback(() => {
    const token = sessionStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('usersLoadError'));
      setUsers(await response.json());
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('usersLoadErrorDetail'));
    } finally {
      setLoading(false);
    }
  }, [buildHeaders, t]);

  const loadRoles = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/roles`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('rolesLoadError'));
      setRoles(await response.json());
    } catch (caught) {
      console.error(caught instanceof Error ? caught.message : t('rolesLoadErrorDetail'));
    }
  }, [buildHeaders, t]);

  const loadCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('companiesLoadError'));
      setCompanies(await response.json());
    } catch (caught) {
      console.error(caught instanceof Error ? caught.message : t('companiesLoadErrorDetail'));
    }
  }, [buildHeaders, t]);

  const loadBillingInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/me`, { headers: buildHeaders() });
      if (!response.ok) return;
      setBillingInfo(await response.json());
    } catch (_caught) {
    }
  }, [buildHeaders]);

  const loadUserAccess = useCallback(async (userId) => {
    setAccessLoading(true);
    setAccessError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/company-access`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('userAccessLoadError'));
      setCompanyAccessEntries(await response.json());
      setAccessDirty(false);
    } catch (caught) {
      setCompanyAccessEntries([]);
      setAccessError(caught instanceof Error ? caught.message : t('userAccessLoadErrorDetail'));
    } finally {
      setAccessLoading(false);
    }
  }, [buildHeaders, t]);

  useEffect(() => {
    loadUsers();
    loadRoles();
    loadCompanies();
    loadBillingInfo();
  }, [loadUsers, loadRoles, loadCompanies, loadBillingInfo]);

  const resetForm = useCallback(() => {
    setFormState(createFormState());
    setFormError('');
    setCompanyAccessEntries([]);
    setSelectedCompanyId('');
    setSelectedAccessLevel('READ_ONLY');
    setAccessError('');
    setAccessSuccess('');
    setAccessDirty(false);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const preparePayload = useCallback(() => {
    const payload = {
      username: formState.username.trim(),
      fullName: formState.fullName.trim(),
      email: formState.email.trim(),
      role: normalizeRoleName(formState.role),
      status: formState.status,
      tenantUserMode: normalizeRoleName(formState.role) === 'TENANT_USER' ? (formState.tenantUserMode || 'READONLY') : null
    };
    const password = formState.password.trim();
    if (password) payload.password = password;
    return payload;
  }, [formState]);

  const isEditMode = Boolean(formState.id);
  const currentUserRole = normalizeRoleName(sessionStorage.getItem('authRole'));
  const isLoggedTenantAdmin = currentUserRole === 'TENANT_ADMIN';
  const normalizedRole = normalizeRoleName(formState.role);
  const isSystemAdmin = normalizedRole === 'SYS_ADMIN';
  const isTenantAdmin = Boolean(formState.tenantAdmin) || normalizedRole === 'TENANT_ADMIN';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparePayload();
    if (!payload.username || !payload.fullName || !payload.email || !payload.role || (!isEditMode && !payload.password)) {
      setFormError(t('requiredFields'));
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/users${isEditMode ? `/${formState.id}` : ''}`, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('saveFailed'));
      }
      setSuccess(isEditMode ? t('userUpdated') : t('userCreated'));
      closeModal();
      await loadUsers();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationError'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    if (isLoggedTenantAdmin && normalizeRoleName(user?.role) !== 'TENANT_USER') {
      setListError(t('tenantAdminOnlyEdit'));
      return;
    }
    const isTenantAdminUser = Boolean(user.tenantAdmin);
    const normalizedUserRole = normalizeRoleName(user.role);
    const resolvedRole = normalizedUserRole
      || ((user.username || '').toLowerCase() === 'admin' ? 'SYS_ADMIN' : (isTenantAdminUser ? 'TENANT_ADMIN' : 'TENANT_USER'));

    setFormState({
      id: user.id,
      username: user.username,
      password: '',
      fullName: user.fullName,
      email: user.email,
      role: resolvedRole,
      status: user.status || 'Aktif',
      tenantAdmin: isTenantAdminUser,
      tenantUserMode: user.tenantUserMode || 'READONLY'
    });
    setSuccess('');
    setFormError('');
    setCompanyAccessEntries([]);
    setAccessError('');
    setAccessSuccess('');
    setAccessDirty(false);
    setSelectedCompanyId('');
    setSelectedAccessLevel('READ_ONLY');
    setIsModalOpen(true);
    if (!isTenantAdminUser) loadUserAccess(user.id);
  };

  const handleDelete = async (user) => {
    if (isLoggedTenantAdmin && normalizeRoleName(user?.role) !== 'TENANT_USER') {
      setListError(t('tenantAdminOnlyDelete'));
      return;
    }
    if (!window.confirm(t('confirmDelete'))) return;

    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('deleteFailed'));
      }
      setSuccess(t('userDeleted'));
      if (formState.id === user.id) closeModal();
      await loadUsers();
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('deleteError'));
    } finally {
      setSaving(false);
    }
  };

  const roleOptions = useMemo(() => {
    const names = roles.map((roleItem) => normalizeRoleName(roleItem?.name)).filter(Boolean);
    ['SYS_ADMIN', 'TENANT_ADMIN', 'TENANT_USER'].forEach((coreRole) => {
      if (!names.includes(coreRole)) names.push(coreRole);
    });
    if (isLoggedTenantAdmin) return ['TENANT_USER'];
    const currentRole = normalizeRoleName(formState.role);
    if (currentRole && !names.includes(currentRole)) names.unshift(currentRole);
    return names;
  }, [roles, formState.role, isLoggedTenantAdmin]);

  const accessEntriesSorted = useMemo(
    () => [...companyAccessEntries].sort((a, b) => (companiesById[a.companyId]?.name || '').localeCompare(companiesById[b.companyId]?.name || '')),
    [companyAccessEntries, companiesById]
  );

  const getAccessLabel = useCallback((level) => accessLabelMap[level] || level, [accessLabelMap]);

  const handleAccessAdd = () => {
    setAccessError('');
    setAccessSuccess('');
    if (!formState.id) return setAccessError(t('saveUserFirst'));
    if (!selectedCompanyId) return setAccessError(t('selectCompany'));
    const companyId = Number(selectedCompanyId);
    if (!companyId) return setAccessError(t('invalidCompany'));

    setCompanyAccessEntries((current) => {
      const exists = current.find((entry) => entry.companyId === companyId);
      if (exists) {
        return current.map((entry) => (entry.companyId === companyId ? { ...entry, accessLevel: selectedAccessLevel } : entry));
      }
      return [...current, { companyId, accessLevel: selectedAccessLevel }];
    });
    setSelectedCompanyId('');
    setAccessDirty(true);
  };

  const handleAccessRemove = (companyId) => {
    setCompanyAccessEntries((current) => current.filter((entry) => entry.companyId !== companyId));
    setAccessDirty(true);
    setAccessError('');
    setAccessSuccess('');
  };

  const handleAccessSave = async () => {
    if (!formState.id) {
      setAccessError(t('saveUserFirst'));
      return;
    }
    setAccessSaving(true);
    setAccessError('');
    setAccessSuccess('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/system/users/${formState.id}/company-access`, {
        method: 'PUT',
        headers: buildHeaders(),
        body: JSON.stringify(companyAccessEntries)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('accessSaveFailed'));
      }
      setCompanyAccessEntries(await response.json());
      setAccessSuccess(t('accessSaved'));
      setAccessDirty(false);
    } catch (caught) {
      setAccessError(caught instanceof Error ? caught.message : t('accessSaveError'));
    } finally {
      setAccessSaving(false);
    }
  };

  const isFormValid = formState.username.trim()
    && formState.fullName.trim()
    && formState.email.trim()
    && formState.role.trim()
    && (isEditMode || formState.password.trim());
  const currentUserCount = billingInfo?.currentUserCount ?? 0;
  const maxUsers = billingInfo?.maxUsers ?? null;
  const userLimitText = maxUsers == null
    ? t('userLimitUsageUnlimited').replace('{{current}}', String(currentUserCount))
    : t('userLimitUsage').replace('{{current}}', String(currentUserCount)).replace('{{max}}', String(maxUsers));

  return (
    <div className="card users-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={<button type="button" className="btn-secondary" onClick={openCreateModal}>{t('newUser')}</button>}
      />
      {success && <div className="alert alert-success">{success}</div>}
      {billingInfo && (
        <div className="alert alert-info">
          <strong>{t('userLimitSummary')}:</strong> {maxUsers == null ? t('userLimitUnlimited') : userLimitText}
        </div>
      )}

      <div className="users-grid">
        <section className="users-table-panel">
          <div className="table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t('name')}</th>
                  <th>{t('email')}</th>
                  <th>{t('role')}</th>
                  <th>{t('status')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="table-placeholder">{t('loading')}</td></tr>
                ) : listError ? (
                  <tr><td colSpan={5} className="table-placeholder">{listError}</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={5} className="table-placeholder">{t('noUsers')}</td></tr>
                ) : users.map((user) => {
                  const canManageRow = !isLoggedTenantAdmin || normalizeRoleName(user.role) === 'TENANT_USER';
                  return (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.fullName}</strong>
                        <p className="muted">@{user.username}</p>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td><span className={user.status === 'Aktif' ? 'badge badge-active' : 'badge'}>{statusLabel(user.status)}</span></td>
                      <td className="action-cell">
                        {canManageRow ? (
                          <>
                            <button type="button" className="action-icon-btn action-icon-btn-edit" onClick={() => handleEdit(user)} aria-label={t('edit')} title={t('edit')}>
                              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                              </svg>
                            </button>
                            <button type="button" className="action-icon-btn action-icon-btn-delete" onClick={() => handleDelete(user)} aria-label={t('delete')} title={t('delete')}>
                              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                <path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" />
                              </svg>
                            </button>
                          </>
                        ) : <span className="muted">{t('noPermission')}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editUserTitle') : t('createUserTitle')} width="760px">
          <section>
            {formError && <div className="alert alert-error">{formError}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-field">
                <label htmlFor="username">{t('username')}</label>
                <input id="username" name="username" value={formState.username} onChange={(event) => setFormState((c) => ({ ...c, username: event.target.value }))} placeholder={language.startsWith('en') ? 'customer123' : 'musteri123'} />
              </div>
              <div className="form-field">
                <label htmlFor="fullName">{t('name')}</label>
                <input id="fullName" name="fullName" value={formState.fullName} onChange={(event) => setFormState((c) => ({ ...c, fullName: event.target.value }))} placeholder={language.startsWith('en') ? 'Alice K.' : 'Ayse K.'} />
              </div>
              <div className="form-field">
                <label htmlFor="email">{t('email')}</label>
                <input id="email" name="email" type="email" value={formState.email} onChange={(event) => setFormState((c) => ({ ...c, email: event.target.value }))} placeholder="alice@example.com" />
              </div>
              <div className="form-field">
                <label htmlFor="role">{t('role')}</label>
                <select
                  id="role"
                  name="role"
                  value={normalizeRoleName(formState.role)}
                  onChange={(event) => setFormState((c) => ({ ...c, role: normalizeRoleName(event.target.value), tenantUserMode: normalizeRoleName(event.target.value) === 'TENANT_USER' ? (c.tenantUserMode || 'READONLY') : c.tenantUserMode }))}
                >
                  <option value="">{t('rolePlaceholder')}</option>
                  {roleOptions.map((roleName) => <option key={roleName} value={roleName}>{roleName}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="status">{t('status')}</label>
                <select id="status" name="status" value={formState.status} onChange={(event) => setFormState((c) => ({ ...c, status: event.target.value }))}>
                  {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{statusLabel(option)}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="password">{t('password')}</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formState.password}
                  onChange={(event) => setFormState((c) => ({ ...c, password: event.target.value }))}
                  placeholder={isEditMode ? (language.startsWith('en') ? 'Enter new password to change' : 'Degistirmek icin yeni sifre girin') : t('password')}
                  required={!isEditMode}
                />
              </div>
              <div className="form-footer">
                <button type="submit" className="btn-primary" disabled={!isFormValid || saving}>{saving ? t('saving') : isEditMode ? t('update') : t('save')}</button>
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>{t('cancel')}</button>
              </div>
            </form>
            <p className="muted form-hint">{t('passwordHint')}</p>

            {isEditMode ? (
              isSystemAdmin ? (
                <div className="access-section"><p className="access-empty">{t('systemAdminAccess')}</p></div>
              ) : isTenantAdmin ? (
                <div className="access-section"><p className="access-empty">{t('tenantAdminAccess')}</p></div>
              ) : (
                <div className="access-section">
                  <h3>{t('companyAccesses')}</h3>
                  <div className="access-form-grid">
                    <div className="form-field">
                      <label htmlFor="access-company">{t('companyPlaceholder')}</label>
                      <select id="access-company" value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)} disabled={companies.length === 0}>
                        <option value="">{t('companyPlaceholder')}</option>
                        {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label htmlFor="access-level">{t('accessType')}</label>
                      <select id="access-level" value={selectedAccessLevel} onChange={(event) => setSelectedAccessLevel(event.target.value)}>
                        {accessOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </div>
                    <button type="button" className="btn-primary access-add-button" onClick={handleAccessAdd}>{t('addCompanyAccess')}</button>
                  </div>

                  {accessError && <div className="alert alert-error">{accessError}</div>}
                  {accessSuccess && <div className="alert alert-success">{accessSuccess}</div>}

                  <div className="access-list">
                    {accessLoading ? (
                      <p className="access-empty">{t('loading')}</p>
                    ) : accessEntriesSorted.length === 0 ? (
                      <p className="access-empty">{t('noAccessEntries')}</p>
                    ) : accessEntriesSorted.map((entry) => (
                      <div key={entry.companyId} className="access-entry">
                        <div>
                          <strong>{companiesById[entry.companyId]?.name || `${language.startsWith('en') ? 'Company' : 'Sirket'} #${entry.companyId}`}</strong>
                          <span>{getAccessLabel(entry.accessLevel)}</span>
                        </div>
                        <button type="button" className="access-remove-button" onClick={() => handleAccessRemove(entry.companyId)}>{t('remove')}</button>
                      </div>
                    ))}
                  </div>

                  <div className="access-footer">
                    <button type="button" className="btn-primary" onClick={handleAccessSave} disabled={!accessDirty || accessSaving}>
                      {accessSaving ? t('saving') : t('saveAccesses')}
                    </button>
                  </div>
                </div>
              )
            ) : (
              <p className="muted access-placeholder">{t('accessPlaceholder')}</p>
            )}
          </section>
      </Modal>
    </div>
  );
}

export default UsersPage;
